import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Dialog, DialogContent } from 'shared/components/ui/dialog';
import { Input } from 'shared/components/ui/input';
import { IconNote } from 'shared/components/ui/IconNote';
import { FinecoButton } from 'pages/assistant/components/FinecoButton';
import constraintProfilesStore from 'shared/store/constraintProfilesStore';
import constraintsStore from 'shared/store/constraintsStore';

import styles from './savedProfiles.module.scss';
import { createLogger } from 'shared/logger';

const log = createLogger('SaveProfile');

/**
 * Truncate a profile name to a button-friendly length, suffixing an
 * ellipsis. The "Aggiorna \"<name>\"" button label embeds the profile
 * name and overflows the 420px dialog when the name approaches the
 * 30-char rename limit. The default cap (8) keeps "Aggiorna \"<8 chars>…\""
 * narrow enough that the button + the sibling "Salva come nuovo" button
 * fit inside the dialog without overflow (the `FinecoButton` sizes to
 * content because of the inline `width: auto` override on its CSS).
 * The full untruncated name is still shown via the wrapping span's
 * `title` attribute on hover.
 */
const truncateForButton = (name: string, max = 8): string =>
  name.length > max ? `${name.slice(0, max).trimEnd()}…` : name;

type SaveProfileDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

export const SaveProfileDialog: React.FC<SaveProfileDialogProps> = ({ open, onOpenChange }) => {
  const { t } = useTranslation();
  const [name, setName] = useState('');
  const [isDefault, setIsDefault] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [showOverrideWarning, setShowOverrideWarning] = useState(false);
  const [existingDefaultName, setExistingDefaultName] = useState('');
  // When appliedLiveProfile exists, we show two actions. This flag switches
  // the dialog into "save as new" mode when the user clicks "Salva come nuovo".
  const [showNewNameField, setShowNewNameField] = useState(false);

  const appliedProfile = constraintsStore((s) => s.appliedProfile);
  const mode = constraintsStore((s) => s.mode);
  const profilesByMode = constraintProfilesStore((s) => s.profilesByMode);
  const profiles = profilesByMode[mode] ?? [];

  // A profile that is BOTH applied and still present in the catalog.
  const appliedLiveProfile =
    appliedProfile ? profiles.find((p) => p.id === appliedProfile.id) ?? null : null;

  const handleIsDefaultChange = (checked: boolean) => {
    setIsDefault(checked);
    if (checked) {
      // When updating an existing profile, exclude itself from the search so
      // we only warn when a *different* profile already holds the default flag.
      const existingDefault = profiles.find(
        (p) =>
          p.is_default &&
          (!appliedLiveProfile || p.id !== appliedLiveProfile.id),
      );
      if (existingDefault) {
        setExistingDefaultName(existingDefault.name);
        setShowOverrideWarning(true);
        return;
      }
      // No saved profile holds the default ⇒ "Predefiniti" (the built-in
      // default) is the current startup default. Flagging this config as
      // default still changes the default, so warn uniformly — the same way
      // we warn when switching between two user configs. Skip only when the
      // profile being updated is *already* the default (no real change).
      if (!appliedLiveProfile?.is_default) {
        setExistingDefaultName(t('savedProfiles.defaults'));
        setShowOverrideWarning(true);
        return;
      }
    }
    setShowOverrideWarning(false);
    setExistingDefaultName('');
  };

  const handleUpdate = async () => {
    if (!appliedLiveProfile || submitting) return;
    const workingDocument = constraintsStore.getState().workingDocument;
    setSubmitting(true);
    try {
      const updated = await constraintProfilesStore
        .getState()
        .updateProfile(appliedLiveProfile.id, {
          constraints: workingDocument ?? undefined,
          is_default: isDefault,
        });
      constraintsStore.getState().onProfileSaved(updated);
      resetForm();
      onOpenChange(false);
    } catch (error) {
      log.error('update constraint profile failed', { error });
    } finally {
      setSubmitting(false);
    }
  };

  const handleSaveAsNew = async () => {
    const trimmedName = name.trim();
    if (!trimmedName || submitting) return;
    const workingDocument = constraintsStore.getState().workingDocument;
    setSubmitting(true);
    try {
      const created = await constraintProfilesStore.getState().createProfile({
        name: trimmedName,
        mode,
        is_default: isDefault,
        constraints: workingDocument ?? {},
      });
      constraintsStore.getState().onProfileSaved(created);
      resetForm();
      onOpenChange(false);
    } catch (error) {
      log.error('create constraint profile failed', { error });
    } finally {
      setSubmitting(false);
    }
  };

  const resetForm = () => {
    setName('');
    setIsDefault(false);
    setShowOverrideWarning(false);
    setExistingDefaultName('');
    setShowNewNameField(false);
  };

  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen) resetForm();
    onOpenChange(newOpen);
  };

  // ── Two-action branch: applied live profile + user has NOT yet chosen "Salva come nuovo"
  if (appliedLiveProfile && !showNewNameField) {
    return (
      <Dialog open={open} onOpenChange={handleOpenChange}>
        <DialogContent title={t('savedProfiles.saveDialogTitle')} className={styles.saveDialog}>
          <div className={styles.saveDialogBody}>
            <div className={styles.checkboxContainer}>
              <input
                type="checkbox"
                id="applyOnStartup"
                checked={isDefault}
                onChange={(e) => handleIsDefaultChange(e.target.checked)}
                disabled={submitting}
                className={styles.checkbox}
              />
              <label htmlFor="applyOnStartup" className={styles.checkboxLabel}>
                {t('savedProfiles.applyOnStartup')}
              </label>
            </div>

            {showOverrideWarning && (
              <IconNote
                type="attention"
                title="Attenzione"
                body={t('savedProfiles.overrideWarningNamed', { name: existingDefaultName })}
              />
            )}
          </div>

          <div className={styles.saveDialogFooter}>
            {/* Truncate the embedded profile name for the button label so a
                30-character name doesn't overflow the 420px dialog. The
                wrapping span carries the untruncated label on hover via
                `title` so the user can still see the full name. */}
            <span
              title={t('savedProfiles.updateExisting', {
                name: appliedLiveProfile.name,
              })}
            >
              <FinecoButton
                click={handleUpdate}
                copy={
                  submitting
                    ? t('savedProfiles.saving')
                    : t('savedProfiles.updateExisting', {
                        name: truncateForButton(appliedLiveProfile.name),
                      })
                }
                disabled={submitting}
              />
            </span>
            <FinecoButton
              click={() => setShowNewNameField(true)}
              copy={t('savedProfiles.saveAsNew')}
              disabled={submitting}
            />
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  // ── Save-as-new branch: no live profile applied, OR user chose "Salva come nuovo"
  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent title={t('savedProfiles.saveDialogTitle')} className={styles.saveDialog}>
        <div className={styles.saveDialogBody}>
          <div className={styles.nameInputContainer}>
            <Input
              id="profileName"
              value={name}
              onChange={(e) => setName(e.target.value.slice(0, 30))}
              placeholder={t('savedProfiles.namePlaceholder')}
              disabled={submitting}
              autoFocus
            />
            <span className={styles.charLimit}>
              {t('savedProfiles.maxChars', { max: 30 })}
            </span>
          </div>

          <div className={styles.checkboxContainer}>
            <input
              type="checkbox"
              id="applyOnStartup"
              checked={isDefault}
              onChange={(e) => handleIsDefaultChange(e.target.checked)}
              disabled={submitting}
              className={styles.checkbox}
            />
            <label htmlFor="applyOnStartup" className={styles.checkboxLabel}>
              {t('savedProfiles.applyOnStartup')}
            </label>
          </div>

          {showOverrideWarning && (
            <IconNote
              type="attention"
              title="Attenzione"
              body={t('savedProfiles.overrideWarningNamed', { name: existingDefaultName })}
            />
          )}
        </div>

        <div className={styles.saveDialogFooter}>
          <FinecoButton
            click={handleSaveAsNew}
            copy={submitting ? t('savedProfiles.saving') : t('savedProfiles.saveAsNew')}
            disabled={!name.trim() || submitting}
          />
        </div>
      </DialogContent>
    </Dialog>
  );
};
